import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { usePlan } from '../context/PlanContext';
import { useAnalysisAllowance } from '../hooks/useAnalysisAllowance';
import { useStripe } from '../context/StripeContext';
import { getPendingPaymentNotification, savePendingPaymentNotification } from '../hooks/usePaymentNotification';
import { useUserRole } from '../hooks/useUserRole';
import AppShell from '../components/AppShell';
import SEO from '../components/SEO';
import SupportIdDisplay from '../components/SupportIdDisplay';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import CreditUsagePanel from '../components/CreditUsagePanel';

import {
  ArrowRight,
  Calendar,
  ChartBar,
  CheckCircle,
  CircleNotch,
  Clock,
  DownloadSimple,
  Envelope,
  FloppyDisk,
  GearSix,
  Info,
  Key,
  Lightning,
  Lock,
  Shield,
  Sliders,
  Trash,
  User,
  Warning,
  X
} from '@phosphor-icons/react';
import { WorkspaceHeader, WorkspaceContent } from '../components/WorkspaceLayout';
const Account = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { plan, refreshPlan } = usePlan();
  const { activeOrganization } = useOrganization();
  const { role, isAdmin } = useUserRole();
  const { allowance: analysisAllowance, loading: allowanceLoading, fetchAllowance } = useAnalysisAllowance();
  const { pollPaymentStatus } = useStripe();
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditLoading, setCreditLoading] = useState(true);
  const [showCreditPanel, setShowCreditPanel] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const paymentProcessed = useRef(false);
  const [commandDefaultView, setCommandDefaultView] = useState(false);
  const [prefLoading, setPrefLoading] = useState(false);
  
  // Check if viewing primary organization
  const isPrimaryOrg = activeOrganization?.is_primary === true;
  
  // Check if user is a member (not admin)
  const isMemberRole = role === 'member' || role === 'employee' || role === 'viewer';

  // Handle payment success with verification
  const handlePaymentSuccess = useCallback(async (sessionId, purchaseType) => {
    setPaymentProcessing(true);
    try {
      const result = await pollPaymentStatus(sessionId);
      
      if (result.success) {
        if (purchaseType === 'analysis_pack') {
          toast.success('Analysis pack purchased successfully! Your balance has been updated.');
          // Refresh allowance data
          if (fetchAllowance) {
            await fetchAllowance();
          }
        } else {
          toast.success('Subscription activated successfully! Welcome to your new plan.');
          // Refresh plan data
          if (refreshPlan) {
            await refreshPlan();
          }
          // Also refetch profile to get updated plan
          await fetchProfile();
        }
      } else {
        toast.error(result.error || 'Payment verification failed. Please contact support if you were charged.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error('Could not verify payment status. Please refresh the page.');
    } finally {
      setPaymentProcessing(false);
    }
  }, [pollPaymentStatus, fetchAllowance, refreshPlan]);

  // Handle payment callback - robust implementation with localStorage
  useEffect(() => {
    // Skip if already processed in this session
    if (paymentProcessed.current) return;
    
    // First check localStorage for pending notification (survives page reload)
    const pending = getPendingPaymentNotification();
    if (pending && pending.type === 'success') {
      paymentProcessed.current = true;
      handlePaymentSuccess(pending.sessionId, pending.purchaseType);
      return;
    }
    
    // Check URL params for payment callback
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const purchaseType = searchParams.get('type');
    
    if (paymentStatus === 'success' && sessionId) {
      paymentProcessed.current = true;
      // FloppyDisk to localStorage immediately in case of remount
      savePendingPaymentNotification('success', sessionId, purchaseType);
      // Clear URL params
      setSearchParams({});
      // Process payment
      handlePaymentSuccess(sessionId, purchaseType);
    } else if (paymentStatus === 'cancelled') {
      paymentProcessed.current = true;
      setSearchParams({});
      toast.info('Payment was cancelled. No charges were made.');
    }
  }, [searchParams, setSearchParams, handlePaymentSuccess]);

  useEffect(() => {
    fetchProfile();
  }, []);

  // Fetch credit balance
  useEffect(() => {
    const fetchCredits = async () => {
      if (!activeOrganization?.organization_id) return;
      try {
        const res = await fetchWithOrg(`${BACKEND_URL}/api/credits/balance`, {}, activeOrganization.organization_id);
        if (res.ok) {
          const data = await res.json();
          setCreditBalance(data.credit_balance ?? 0);
        }
      } catch (e) {
        console.warn('Failed to fetch credit balance:', e);
      } finally {
        setCreditLoading(false);
      }
    };
    fetchCredits();
  }, [activeOrganization?.organization_id]);


  const fetchProfile = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/account/profile`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setFormData({ name: data.name });
        setCommandDefaultView(!!data.preferences?.command_default_view);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCommandDefault = async () => {
    const newValue = !commandDefaultView;
    setCommandDefaultView(newValue);
    setPrefLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/account/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ command_default_view: newValue }),
      });
      if (resp.ok) {
        toast.success(newValue ? 'Command will open on startup' : 'Command startup disabled');
      } else {
        setCommandDefaultView(!newValue);
        toast.error('Failed to update preference');
      }
    } catch {
      setCommandDefaultView(!newValue);
      toast.error('Failed to update preference');
    } finally {
      setPrefLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/account/profile`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: formData.name })
        }
      );

      if (response.ok) {
        toast.success('Profile updated successfully');
        await fetchProfile();
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Export Data - DISABLED for Public Beta
  // Feature coming in future release - no API call should be made
  // const handleExportData = () => { /* Disabled */ };

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
    setDeleteConfirmation('');
  };

  const confirmDelete = async () => {
    if (deleteConfirmation !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" exactly to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/account/delete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ confirmation: deleteConfirmation })
        }
      );

      if (response.ok) {
        toast.success('Account deleted successfully. Redirecting...');
        // Clear any local storage
        localStorage.clear();
        sessionStorage.clear();
        // Wait a moment then redirect to home
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to delete account');
        setIsDeleting(false);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPlanDisplayName = () => {
    if (!plan) return 'Starter';
    if (plan === 'starter') return 'Starter';
    if (plan === 'basic') return 'Basic';
    if (plan === 'pro') return 'Pro';
    if (plan === 'partner_pro' || plan === 'partner-pro') return 'Partner Pro';
    return plan.charAt(0).toUpperCase() + plan.slice(1); // Fallback: capitalize
  };

  const getPlanBadgeColor = () => {
    if (plan === 'partner_pro' || plan === 'partner-pro') return 'bg-[#6366F1]';
    if (plan === 'pro') return 'bg-[#6366F1]';
    if (plan === 'basic') return 'bg-[#5C6778]';
    return 'bg-[#718096]'; // starter
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-20">
          <CircleNotch size={32} className="text-white animate-spin" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <SEO title="Account Settings - Man & Machine" />

      <div className="min-h-screen bg-[var(--surface-0)]">
        <WorkspaceHeader
          icon={GearSix}
          title="Account Settings"
          description="Manage your profile, security, and account preferences"
        />

        <WorkspaceContent>
        <div className="max-w-3xl space-y-6">
          {/* Profile Section */}
          <div
            className="rounded-[10px] bg-[var(--surface-2)] p-6 border border-[var(--border-default)]"
          >
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-8 h-8 rounded-lg bg-[rgba(99,102,241,0.08)] flex items-center justify-center">
                <User size={16} weight="duotone" className="text-[#818CF8]" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-white">Profile</h2>
                <p className="text-[11px] text-[var(--text-muted)]">Your personal information</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Name Input */}
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-md bg-[var(--surface-4)] border border-[var(--border-default)] text-white text-sm focus:outline-none focus:border-[#6366F1]/50 transition-colors"
                  placeholder="Your name"
                />
              </div>

              {/* Email (Read-only) */}
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                  Email Address
                </label>
                <div className="flex items-center gap-3 px-3.5 py-2.5 rounded-md bg-[var(--surface-0)] border border-[var(--border-subtle)]">
                  <Envelope size={14} className="text-[var(--text-muted)]" />
                  <span className="text-sm text-[var(--text-secondary)]">{user?.email}</span>
                  <span className="ml-auto text-[10px] text-[var(--text-muted)]">Read-only</span>
                </div>
              </div>

              {/* Plan Badge */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Current Plan
                </label>
                <div className="inline-block">
                  <div className={`px-4 py-2 rounded-full ${getPlanBadgeColor()} text-white font-semibold text-sm`}>
                    {getPlanDisplayName()}
                  </div>
                </div>
              </div>

              {/* Member Since */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Member Since
                </label>
                <div className="flex items-center gap-3 text-[#A0AEC0]">
                  <Calendar size={18} />
                  <span>{formatDate(user?.created_at)}</span>
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={saving || formData.name === user?.name}
                className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 text-white text-sm font-medium rounded-md transition-colors"
              >
                {saving ? (
                  <>
                    <CircleNotch size={14} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FloppyDisk size={14} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Security Section */}
          <div
            className="rounded-[10px] bg-[var(--surface-2)] p-6 border border-[var(--border-default)]"
          >
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-8 h-8 rounded-lg bg-[rgba(139,92,246,0.08)] flex items-center justify-center">
                <Shield size={16} weight="duotone" className="text-[#A78BFA]" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-white">Security</h2>
                <p className="text-[11px] text-[var(--text-muted)]">Manage your account security</p>
              </div>
            </div>

            <div className="space-y-3">
              {/* Reset Password */}
              <div className="flex items-center justify-between p-3.5 rounded-md bg-[var(--surface-0)] border border-[var(--border-subtle)] hover:border-[var(--border-default)] transition-colors">
                <div className="flex items-center gap-3">
                  <Key size={16} className="text-[var(--text-muted)]" />
                  <div>
                    <p className="text-sm text-white font-medium">Password</p>
                    <p className="text-[11px] text-[var(--text-muted)]">Change your password</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/forgot-password')}
                  className="px-3 py-1.5 rounded-md bg-[var(--surface-3)] border border-[var(--border-default)] text-white text-xs font-medium hover:bg-[var(--surface-4)] transition-colors"
                >
                  Reset Password
                </button>
              </div>

              {/* 2FA (Placeholder) */}
              <div className="flex items-center justify-between p-3.5 rounded-md bg-[var(--surface-0)] border border-[var(--border-subtle)]">
                <div className="flex items-center gap-3">
                  <Lock size={16} className="text-[var(--text-muted)]" />
                  <div>
                    <p className="text-sm text-white font-medium">Two-Factor Authentication</p>
                    <p className="text-[11px] text-[var(--text-muted)]">Coming soon</p>
                  </div>
                </div>
                <span className="px-3 py-1.5 rounded-md bg-[var(--surface-3)] text-[var(--text-muted)] text-xs">
                  Soon
                </span>
              </div>
            </div>
          </div>

          {/* Support Information Section - PUBLIC BETA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            {/* Preferences Section */}
            <div className="rounded-[10px] bg-[var(--surface-2)] p-6 border border-[var(--border-default)] mb-6">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-lg bg-[rgba(99,102,241,0.08)] flex items-center justify-center">
                  <Sliders size={16} weight="duotone" className="text-[#818CF8]" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Preferences</h2>
                  <p className="text-[11px] text-[var(--text-muted)]">Personalize your experience</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3.5 rounded-md bg-[var(--surface-0)] border border-[var(--border-subtle)] hover:border-[var(--border-default)] transition-colors">
                  <div className="flex items-center gap-3">
                    <Lightning size={16} weight="duotone" className="text-[#818CF8]" />
                    <div>
                      <p className="text-sm text-white font-medium">Open Command on startup</p>
                      <p className="text-[11px] text-[var(--text-muted)]">Automatically launch Command when you log in</p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleCommandDefault}
                    disabled={prefLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                      commandDefaultView ? 'bg-[#6366F1]' : 'bg-[var(--surface-4)]'
                    }`}
                    data-testid="command-default-toggle"
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                      commandDefaultView ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <SupportIdDisplay 
              userId={user?.id} 
              organizationId={user?.organization_id}
            />
          </motion.div>

          {/* Data Section - Only show for admins */}
          {!isMemberRole && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="rounded-[10px] bg-[var(--surface-2)] p-6 border border-[var(--border-default)]"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-[rgba(20,241,149,0.08)] flex items-center justify-center">
                  <DownloadSimple size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Data</h2>
                  <p className="text-[11px] text-[var(--text-muted)]">Export and manage your data</p>
                </div>
              </div>

              {/* Export Data - Coming Soon (Disabled for Public Beta) */}
              <div className="relative group/export">
                <div className="flex items-center justify-between p-4 rounded-xl bg-[rgba(255,255,255,0.02)] opacity-60 cursor-not-allowed">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">Download My Data</p>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/20 text-[#6366F1] text-xs font-medium">
                        <Clock size={10} />
                        Coming Soon
                      </span>
                    </div>
                    <p className="text-[#718096] text-sm mt-1">Export all your scans and processes</p>
                    <p className="text-[#6366F1]/70 text-xs mt-2 italic">
                      Data export will be available in a future release to support collaboration and portability.
                    </p>
                  </div>
                  <div 
                    className="px-4 py-2 rounded-full glass text-gray-500 text-sm font-semibold cursor-not-allowed select-none"
                    aria-disabled="true"
                  >
                    Export Data
                  </div>
                </div>
                {/* Desktop Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-xs text-gray-300 rounded-lg whitespace-nowrap opacity-0 group-hover/export:opacity-100 transition-opacity pointer-events-none z-10 hidden sm:block">
                  <div className="flex items-center gap-1.5">
                    <Info size={12} />
                    Coming soon — export/import will be available on eligible plans.
                  </div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Credits Section - Only show for primary organization, paid plans, and admin users */}
          {!isMemberRole && isPrimaryOrg && plan && !['basic', 'starter'].includes(plan) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="rounded-[10px] bg-[var(--surface-2)] p-6 border border-[rgba(99,102,241,0.2)]"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#6366F1] flex items-center justify-center">
                  <ChartBar size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Credits</h2>
                  <p className="text-[11px] text-[var(--text-muted)]">Your credit balance for AI agent executions and analysis</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Balance display */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/5">
                  <div>
                    <p className="text-xs text-[#A0AEC0] mb-1">Available Balance</p>
                    <p className="text-2xl font-bold text-white">
                      {creditLoading ? (
                        <CircleNotch className="w-5 h-5 animate-spin text-[#6366F1] inline" />
                      ) : (
                        <>{creditBalance?.toLocaleString() ?? 0} <span className="text-sm font-normal text-[#A0AEC0]">credits</span></>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowCreditPanel(true)}
                    data-testid="view-credit-history-btn"
                    className="px-4 py-2 rounded-lg bg-[#6366F1]/10 text-[#6366F1] text-sm font-medium hover:bg-[#6366F1]/20 transition-colors"
                  >
                    View History
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Credit Usage Panel (slides out) */}
          <CreditUsagePanel isOpen={showCreditPanel} onClose={() => setShowCreditPanel(false)} />

          {/* Danger Zone */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-[10px] bg-[var(--surface-2)] p-6 border border-[rgba(239,68,68,0.3)]"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#EF4444] to-[#DC2626] flex items-center justify-center">
                <Warning size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-white">Danger Zone</h2>
                <p className="text-[11px] text-[var(--text-muted)]">Irreversible actions</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl bg-[rgba(239,68,68,0.05)] border border-[rgba(239,68,68,0.2)]">
              <div>
                <p className="text-white font-medium">Delete Account</p>
                <p className="text-[#718096] text-sm">Permanently delete your account and all data</p>
              </div>
              <button
                onClick={handleDeleteAccount}
                className="px-4 py-2 rounded-full bg-[#EF4444] text-white text-sm font-semibold hover:bg-[#DC2626] transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      </WorkspaceContent>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-md rounded-[10px] bg-[var(--surface-2)] p-6"
            >
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className="absolute top-4 right-4 text-[#718096] hover:text-white transition-colors disabled:opacity-50"
              >
                <X size={24} />
              </button>

              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#EF4444] to-[#DC2626] flex items-center justify-center mx-auto mb-6">
                <Warning size={32} className="text-white" />
              </div>

              <h3 className="text-sm font-semibold text-white text-center mb-3">
                Delete Account Permanently?
              </h3>
              
              <div className="bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] rounded-xl p-4 mb-6">
                <p className="text-[#EF4444] text-sm font-semibold mb-2">⚠️ This action cannot be undone!</p>
                <p className="text-[11px] text-[var(--text-muted)]">
                  All your data will be permanently deleted:
                </p>
                <ul className="text-[11px] text-[var(--text-muted)] mt-2 space-y-1 list-disc list-inside">
                  <li>All assessments and scans</li>
                  <li>All opportunities and processes</li>
                  <li>All AI agents and conversations</li>
                  <li>All organization data</li>
                  <li>Your account and profile</li>
                </ul>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-white mb-2">
                  Type <span className="font-mono bg-[rgba(239,68,68,0.2)] px-2 py-1 rounded text-[#EF4444]">DELETE MY ACCOUNT</span> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  disabled={isDeleting}
                  placeholder="DELETE MY ACCOUNT"
                  className="w-full px-4 py-3 rounded-xl glass text-white placeholder:text-[#718096] focus:outline-none focus:ring-2 focus:ring-[#EF4444] transition-all disabled:opacity-50"
                />
                {deleteConfirmation && deleteConfirmation !== 'DELETE MY ACCOUNT' && (
                  <p className="text-[#EF4444] text-xs mt-2">
                    Text doesn&apos;t match. Please type exactly: DELETE MY ACCOUNT
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="flex-1 py-3 rounded-full glass text-white font-semibold hover:bg-[rgba(255,255,255,0.1)] transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting || deleteConfirmation !== 'DELETE MY ACCOUNT'}
                  className="flex-1 py-3 rounded-full bg-[#EF4444] text-white font-semibold hover:bg-[#DC2626] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <CircleNotch size={20} className="animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash size={20} />
                      Delete Forever
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </AppShell>
  );
};

export default Account;
